/**
 * Settings → My connections (ADR-003 §9; the API's GOOGLE_DEFAULT_REDIRECT lands here after Google).
 * The prototype has no Google screen, so this follows its Settings → Integrations idiom (L4434): one panel per
 * service, the state as a chip in the panel head, a plain sentence on what is read.
 *  - Gmail and Google Calendar: GET /me/google; Connect is a link to GET /auth/google/connect (302 to Google);
 *    Disconnect is DELETE /me/google/{scope} (Google revokes both at once — the pop-up says so).
 *  - Managers (team.read) also get "Who has connected": GET /integrations/google, with "Read now"
 *    (POST /integrations/google/{user_id}/resync) for someone whose Gmail works.
 * Connect and Disconnect are the signed-in person's own; while viewing or acting as someone they are not offered.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  GOOGLE_CONNECT_ERROR_MESSAGE,
  GoogleConnectError,
  ROLE_LABEL,
  type GoogleCoverage,
  type GoogleScope,
  type MyGoogleConnection,
} from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import {
  BACKFILL_LABEL,
  CONNECTIONS_PATH,
  GOOGLE_STATE_CHIP,
  googleConnectHref,
  useDisconnectGoogle,
  useGoogleCoverage,
  useMyGoogle,
  useResyncGoogle,
} from '../../lib/google';
import { agoLabel, whenLabel } from '../../lib/mail';
import { plural } from '../../lib/format';
import { Avatar, Empty, ErrorState, Loading, Modal, errorText, useToast } from '../../ui/kit';

type ScopeStatus = MyGoogleConnection['gmail'];

const SCOPE_COPY: Record<GoogleScope, { title: string; what: string; rules: string[] }> = {
  gmail: {
    title: 'Gmail',
    what: 'Replies and silence on your clients’ email threads show on Clients → Emails and count toward client health.',
    rules: [
      'Read-only: we can never send, delete or change an email.',
      'Only threads with a client’s email domain are read; nothing else in your inbox.',
      'We keep the subject, who it was with and a short snippet, never the body.',
    ],
  },
  calendar: {
    title: 'Google Calendar',
    what: 'Your meetings fill in your plan for the day and the meeting prompts.',
    rules: ['Read-only: we can never create, change or delete an event.'],
  },
};

export function Connections() {
  const { can } = useSession();
  const [sp, setSp] = useSearchParams();
  const me = useMyGoogle();
  const managers = can('team.read');
  const err = sp.get('google_error');

  return (
    <>
      {err ? (
        <div className="note bad" role="alert" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>
            {GoogleConnectError.safeParse(err).success
              ? GOOGLE_CONNECT_ERROR_MESSAGE[err as GoogleConnectError]
              : 'Connecting did not finish. Press Connect to try again.'}
          </span>
          <button
            className="btn sm ghost"
            onClick={() =>
              setSp(
                (p) => {
                  p.delete('google_error');
                  return p;
                },
                { replace: true },
              )
            }
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {me.isPending ? (
        <div className="panel">
          <Loading label="Loading your connections…" />
        </div>
      ) : me.isError ? (
        <div className="panel">
          <ErrorState error={me.error} onRetry={() => void me.refetch()} />
        </div>
      ) : (
        <MyConnections c={me.data} />
      )}
      {managers ? <Coverage /> : null}
    </>
  );
}

// ---------------------------------------------------------------- mine

function MyConnections({ c }: { c: MyGoogleConnection }) {
  const { me, readOnly } = useSession();
  const [asking, setAsking] = useState<GoogleScope | null>(null);
  // Connect and Disconnect act on the signed-in person's own Google account; the API refuses both while acting as someone.
  const own = !me.acting_as;
  const missing = (['gmail', 'calendar'] as const).filter((s) => c[s].state !== 'connected');
  return (
    <>
      {c.required && missing.length ? (
        <div className="note warn" style={{ marginBottom: 12 }}>
          Ops roles connect {missing.length === 2 ? 'Gmail and Google Calendar' : SCOPE_COPY[missing[0]!].title}. Until you do, {missing.includes('gmail') ? 'your clients have no email signals' : 'your day plan has no meetings in it'}.
        </div>
      ) : null}
      <div className="cols c2">
        {(['gmail', 'calendar'] as const).map((s) => (
          <ScopePanel
            key={s}
            scope={s}
            st={c[s]}
            backfill={s === 'gmail' ? BACKFILL_LABEL[c.backfill] : null}
            canAct={own && !readOnly}
            onDisconnect={() => setAsking(s)}
          />
        ))}
      </div>
      {!own ? (
        <div className="note" style={{ marginTop: 12 }}>
          You are {me.acting_as?.mode === 'view' ? 'viewing' : 'acting'} as {me.acting_as?.as.name}. Only they can connect or disconnect their Google account.
        </div>
      ) : null}
      {asking ? <DisconnectModal scope={asking} onClose={() => setAsking(null)} /> : null}
    </>
  );
}

function ScopePanel(props: { scope: GoogleScope; st: ScopeStatus; backfill: string | null; canAct: boolean; onDisconnect: () => void }) {
  const { scope, st } = props;
  const copy = SCOPE_COPY[scope];
  const [cls, label] = GOOGLE_STATE_CHIP[st.state];
  const on = st.state === 'connected';
  const blocked = st.state === 'admin_blocked';
  const again = st.state === 'needs_reconnect' || st.state === 'revoked';
  return (
    <div className="panel" style={{ alignSelf: 'start' }}>
      <div className="hd">
        <h3>{copy.title}</h3>
        <span className={`chip ${cls}`}>{label}</span>
      </div>
      <div className="bd" style={{ display: 'grid', gap: 10 }}>
        <div className="sub" style={{ color: 'var(--sub)' }}>
          {copy.what}
        </div>
        <ul className="sub" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, color: 'var(--sub)', fontSize: 12.5 }}>
          {copy.rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        {on ? (
          <div style={{ fontSize: 13 }}>
            {st.connected_at ? `Connected ${whenLabel(st.connected_at)}. ` : ''}
            {st.last_success_at ? `Last read ${agoLabel(st.last_success_at)}.` : 'Not read yet.'}
          </div>
        ) : st.problem ? (
          <div className={`note ${blocked || again ? 'warn' : ''}`} style={{ margin: 0 }}>
            {blocked ? GOOGLE_CONNECT_ERROR_MESSAGE.admin_blocked : st.problem}
          </div>
        ) : null}
        {props.backfill && on ? <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>{props.backfill}</div> : null}
        {props.canAct && !blocked ? (
          <div className="split" style={{ justifyContent: 'flex-end' }}>
            {on ? (
              <button className="btn sm" onClick={props.onDisconnect}>
                Disconnect
              </button>
            ) : (
              <a className="btn sm pri" href={googleConnectHref(scope, CONNECTIONS_PATH)}>
                {again ? 'Connect again' : `Connect ${copy.title}`}
              </a>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Revoking at Google removes BOTH scopes (ADR-003 §5.3), and this person's mailbox-only email rows go (§5.4). */
export function DisconnectModal({ scope, onClose }: { scope: GoogleScope; onClose: () => void }) {
  const dis = useDisconnectGoogle();
  const toast = useToast();
  return (
    <Modal
      title="Disconnect Google?"
      sub={`Google removes Gmail and Google Calendar together, so both stop${scope === 'calendar' ? ', not only Calendar' : ', not only Gmail'}. Email threads that only your mailbox brought in are deleted here; threads a colleague also sees stay.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={dis.isPending}
            onClick={() =>
              dis.mutate(scope, {
                onSuccess: (r) => {
                  toast(
                    r.deleted_threads
                      ? `Disconnected. ${plural(r.deleted_threads, 'email thread')} only your mailbox brought in ${r.deleted_threads === 1 ? 'was' : 'were'} removed.`
                      : 'Disconnected. Gmail and Google Calendar are no longer read.',
                  );
                  onClose();
                },
                onError: (e) => toast(errorText(e)),
              })
            }
          >
            {dis.isPending ? 'Disconnecting…' : 'Disconnect both'}
          </button>
        </>
      }
    >
      {null}
    </Modal>
  );
}

// ---------------------------------------------------------------- managers: coverage

const hoursLabel = (h: number) => (h < 48 ? `${h} h` : `${Math.round(h / 24)} days`);

function Coverage() {
  const q = useGoogleCoverage();
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="hd">
        <h3>Who has connected</h3>
        <div className="r">{q.data ? `As of ${agoLabel(q.data.as_of)}` : null}</div>
      </div>
      {q.isPending ? (
        <Loading label="Loading who has connected…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <CoverageTable c={q.data} />
      )}
    </div>
  );
}

function CoverageTable({ c }: { c: GoogleCoverage }) {
  const { readOnly } = useSession();
  const resync = useResyncGoogle();
  const toast = useToast();
  if (!c.data.length) return <Empty title="No one here yet">People who look after clients appear here once they have access.</Empty>;
  return (
    <>
      {c.members_not_connected ? (
        <div className="note warn" style={{ margin: '0 18px 12px' }}>
          {plural(c.members_not_connected, 'member')} {c.members_not_connected === 1 ? 'has' : 'have'} not connected email —{' '}
          {plural(c.clients_without_email_signals, 'client')} {c.clients_without_email_signals === 1 ? 'has' : 'have'} no email signals.
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th>Person</th>
              <th>Gmail</th>
              <th>Google Calendar</th>
              <th>Last read</th>
              <th className="tr">Missing for</th>
              <th className="tr">Clients without email signals</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {c.data.map((r) => {
              const [gc, gl] = GOOGLE_STATE_CHIP[r.gmail.state];
              const [cc, cl] = GOOGLE_STATE_CHIP[r.calendar.state];
              const h = r.missing_for_hours;
              return (
                <tr key={r.user.id}>
                  <td>
                    <div className="split">
                      <Avatar user={r.user} size={26} />
                      <div>
                        <b>{r.user.name}</b>
                        <div className="sub">
                          {ROLE_LABEL[r.user.role]}
                          {r.required ? '' : ' · not required'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`chip ${gc}`} title={r.gmail.problem ?? undefined}>
                      {gl}
                    </span>
                  </td>
                  <td>
                    <span className={`chip ${cc}`} title={r.calendar.problem ?? undefined}>
                      {cl}
                    </span>
                  </td>
                  <td className="sub">{r.gmail.last_success_at ? agoLabel(r.gmail.last_success_at) : '—'}</td>
                  <td className="tr">
                    {h === null ? '—' : <span className={`chip ${h >= 72 ? 'cold' : h >= 24 ? 'warm' : ''}`}>{hoursLabel(h)}</span>}
                  </td>
                  <td className="tr num">{r.clients_without_email_signals || '—'}</td>
                  <td className="tr">
                    {!readOnly && r.gmail.state === 'connected' ? (
                      <button
                        className="btn sm"
                        disabled={resync.isPending && resync.variables === r.user.id}
                        onClick={() =>
                          resync.mutate(r.user.id, {
                            onSuccess: () => toast(`Reading ${r.user.name}’s mailbox and calendar now.`),
                            onError: (e) => toast(errorText(e)),
                          })
                        }
                      >
                        Read now
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
